// src/mazot-takip/src/pages/DolumEkle.js
import React, { useEffect, useMemo, useState } from "react";
import { db, auth } from "../firebaseConfig";
import {
  collection, getDocs, addDoc, serverTimestamp, Timestamp,
  query, where, orderBy, limit, doc, getDoc
} from "firebase/firestore";

const ROLES = { VIEW:"view", ONLY_INPUT:"only_input", INPUT_APPROVE:"input_approve", CREATOR:"creator" };

/* ------------------ helpers ------------------ */
function normalize(s=""){
  return s.toString().toUpperCase()
    .replaceAll("İ","I").replaceAll("ı","I").replaceAll("Ş","S").replaceAll("ş","S")
    .replaceAll("Ğ","G").replaceAll("ğ","G").replaceAll("Ü","U").replaceAll("ü","U")
    .replaceAll("Ö","O").replaceAll("ö","O").replaceAll("Ç","C").replaceAll("ç","C")
    .trim();
}
function normalizePlate(s=""){
  return normalize(s).replace(/[^A-Z0-9]/g, "");
}
function formatPlateWithSpaces(s=""){
  const raw = normalizePlate(s);
  const m = raw.match(/^(\d{2})([A-Z]{1,3})(\d{2,4})$/);
  return m ? `${m[1]} ${m[2]} ${m[3]}` : raw;
}
function plateQueryValues(s=""){
  return Array.from(new Set([normalizePlate(s), formatPlateWithSpaces(s)].filter(Boolean)));
}
function parseNumber(v){
  if(v===null||v===undefined) return null;
  const n=Number(String(v).replace(",",".").trim());
  return Number.isFinite(n)?n:null;
}
function fmt(n){ return new Intl.NumberFormat("tr-TR",{maximumFractionDigits:2}).format(Number(n||0)); }
function getDun(){ const d=new Date(); d.setDate(d.getDate()-1); const p=x=>String(x).padStart(2,"0"); return `${d.getFullYear()}-${p(d.getMonth()+1)}-${p(d.getDate())}`; }
function incReceiptNo(s){
  if(!s) return s;
  const m=String(s).match(/(.*?)(\d+)(\D*)$/);
  if(!m) return s;
  const next=String(parseInt(m[2],10)+1).padStart(m[2].length,"0");
  return `${m[1]||""}${next}${m[3]||""}`;
}
function tsToMsAny(v){
  if(!v) return null;
  if (v.toDate) return v.toDate().getTime();
  if (typeof v.seconds === "number") return v.seconds * 1000;
  if (typeof v === "string") {
    const s=v.trim();
    let m=s.match(/^(\d{1,2})[.\-](\d{1,2})[.\-](\d{4})$/);
    if(m){
      const dd=m[1].padStart(2,"0"), MM=m[2].padStart(2,"0"), yyyy=m[3];
      const d=new Date(`${yyyy}-${MM}-${dd}T12:00:00`);
      return isNaN(d.getTime())?null:d.getTime();
    }
    m=s.match(/^(\d{4})[.\-](\d{1,2})[.\-](\d{1,2})$/);
    if(m){
      const yyyy=m[1], MM=m[2].padStart(2,"0"), dd=m[3].padStart(2,"0");
      const d=new Date(`${yyyy}-${MM}-${dd}T12:00:00`);
      return isNaN(d.getTime())?null:d.getTime();
    }
  }
  const d=new Date(v);
  return isNaN(d.getTime())?null:d.getTime();
}

/* ------------------ role ------------------ */
async function fetchUserMazotRole(email){
  try{
    if(!email) return ROLES.VIEW;
    const snap1=await getDoc(doc(db,"users",email));
    let data=snap1.exists()?snap1.data():null;
    if(!data){
      const snap2=await getDocs(query(collection(db,"users"), where("email","==",email)));
      if(!snap2.empty) data=snap2.docs[0].data();
    }
    const m=data?.["mazot-takip"]||data?.["mazot_takip"]||{};
    if(m.olusturucu) return ROLES.CREATOR;
    if(m.giren_onaylayan) return ROLES.INPUT_APPROVE;
    if(m.giren) return ROLES.ONLY_INPUT;
    if(m.izleyen) return ROLES.VIEW;
    return ROLES.VIEW;
  }catch{ return ROLES.VIEW; }
}

/* ------------------ master data ------------------ */
// DolumEkle.js içinde fetchActivePlates fonksiyonunu bul ve bu şekilde güncelle:
async function fetchActivePlates(){
  const qPlates=query(collection(db,"plates"), where("active","==",true));
  const snap=await getDocs(qPlates);
  return snap.docs.map(d=>({id:d.id,...(d.data()||{})}))
    .map(x=>({
      id:x.id, 
      plate:(x.plate||"").toUpperCase(), 
      vehicleTesis:x.tesis||"",
      aracCinsi: x.aracCinsi || "" // <-- BU SATIRIN OLDUĞUNDAN EMİN OL
    }))
    .filter(x=>x.plate)
    .sort((a,b)=>a.plate.localeCompare(b.plate,"tr"));
}

function isActivePersonStrict(p){
  if (typeof p.active === "boolean") return p.active;
  const tarih=p.tarih && typeof p.tarih==="object" ? p.tarih : null;
  if(tarih){
    const girisler=Object.keys(tarih).filter(k=>/^giris/i.test(k));
    const cikislar=Object.keys(tarih).filter(k=>/^cikis/i.test(k));
    if(girisler.length!==cikislar.length) return girisler.length>cikislar.length;
    let lastIn=null,lastOut=null;
    girisler.forEach(k=>{const ms=tsToMsAny(tarih[k]); if(ms!=null) lastIn=lastIn==null?ms:Math.max(lastIn,ms);});
    cikislar.forEach(k=>{const ms=tsToMsAny(tarih[k]); if(ms!=null) lastOut=lastOut==null?ms:Math.max(lastOut,ms);});
    if(lastIn!=null||lastOut!=null){
      if(lastOut==null) return true;
      if(lastIn==null) return false;
      return lastIn>lastOut;
    }
  }
  const girisCand=[p.sonGiris,p.girisTarihi,p.iseGiris,p.giris,p.startDate];
  const cikisCand=[p.sonCikis,p.cikisTarihi,p.istenCikis,p.cikis,p.endDate];
  let lastIn=null,lastOut=null;
  girisCand.forEach(v=>{const ms=tsToMsAny(v); if(ms!=null) lastIn=lastIn==null?ms:Math.max(lastIn,ms);});
  cikisCand.forEach(v=>{const ms=tsToMsAny(v); if(ms!=null) lastOut=lastOut==null?ms:Math.max(lastOut,ms);});
  if(lastIn==null&&lastOut==null) return true;
  if(lastIn!=null&&lastOut==null) return true;
  if(lastIn==null&&lastOut!=null) return false;
  return lastIn>lastOut;
}
async function fetchActivePeople(){
  const snap=await getDocs(collection(db,"personeller"));
  const all=snap.docs.map(d=>({id:d.id,...(d.data()||{})}));
  return all.filter(isActivePersonStrict)
    .map(x=>({id:x.id, ad:x.ad||"", soyad:x.soyad||""}))
    .filter(x=>x.ad)
    .sort((a,b)=>`${a.ad} ${a.soyad}`.localeCompare(`${b.ad} ${b.soyad}`,"tr"));
}
async function fetchActiveTanks(){
  const snap=await getDocs(collection(db,"fuel_tanks"));
  return snap.docs.map(d=>({id:d.id,...(d.data()||{})}))
    .filter(x=>x.active!==false)
    .map(x=>({id:x.id, name:x.name||x.tankAdi||"TANK", tesis:x.tesis||"", capacity:x.capacity||null}));
}
async function fetchTesisler(){
  const snap=await getDocs(collection(db,"tesisler"));
  return snap.docs.map(d=>({id:d.id,...(d.data()||{})}))
    .filter(x=>x.active!==false)
    .map(x=>x.name||x.ad||x.title||"")
    .filter(Boolean);
}

/* ------------------ SON KM mantığı ------------------ */
function getCorrectedKmFromHistory(history){
  if(!Array.isArray(history)) return null;
  let best=null;
  for(const h of history){
    const km=parseNumber(h?.odometerKm);
    if(km==null) continue;
    const atMs=tsToMsAny(h.at) ?? -1;
    if(!best || atMs>best.atMs) best={km,atMs};
  }
  return best?best.km:null;
}

/** INDEX GEREKTİRMEYEN kesin fallback: Sadece vehiclePlate ile çek, client-side seç. */
async function fetchLastOdoByClientFilter(plate){
  // orderBy YOK → index gerekmeden çalışır
  const qx = query(
    collection(db, "fuel_transactions"),
    where("vehiclePlate", "in", plateQueryValues(plate)),
    limit(100)
  );
  const snap = await getDocs(qx);
  if (snap.empty) return null;

  // Önce approved && !deleted, sonra !deleted; en yeni 'date' alanına göre
  const docs = snap.docs
    .map(d => ({ref: d, data: d.data() || {}}))
    .filter(x => x.data); // güvenlik

const pick = (arr, selectedDateMs) => {
    let best = null;
    for (const x of arr) {
      const fixed = getCorrectedKmFromHistory(x.data.history);
      const base  = parseNumber(x.data.odometerKm);
      const km    = fixed != null ? fixed : base;
      if (km == null) continue;
      
      const dms = tsToMsAny(x.data.date) ?? -1;
      // Sadece seçilen tarihten küçük veya eşit olanları dikkate al
      if (dms <= selectedDateMs) {
        if (!best || dms > best.dms) best = { km, dms };
      }
    }
    return best ? best.km : null;
  };

  // 1) approved && !deleted
  const approved = docs.filter(x => x.data.deleted === false && String(x.data.status||"").toLowerCase()==="approved");
  const r1 = pick(approved);
  if (r1 != null) return r1;

  // 2) sadece !deleted
  const nonDeleted = docs.filter(x => x.data.deleted === false);
  const r2 = pick(nonDeleted);
  if (r2 != null) return r2;

  return null;
}

/** Tarih bazlı son KM ve Çalışma Saatlerini getiren güncel fonksiyon */
/** Tarih bazlı son KM ve Çalışma Saatlerini getiren EN GÜNCEL fonksiyon */
async function fetchLastDataResilient(plate, selectedDate) {
  if (!plate) return { km: null, h1: null, h2: null };
  
  // Seçilen tarihi milisaniyeye çevir
  const selectedMs = tsToMsAny(selectedDate) || Date.now();

  try {
    // 1) Plakaya ait tüm aktif kayıtları çek (limit artırıldı)
    const q = query(
      collection(db, "fuel_transactions"),
      where("vehiclePlate", "in", plateQueryValues(plate)),
      where("deleted", "==", false)
    );
    
    const snap = await getDocs(q);
    if (snap.empty) return { km: null, h1: null, h2: null };

    // 2) Çekilen verileri bir diziye al ve milisaniye tarihini ekle
    const allRecords = snap.docs.map(d => {
      const data = d.data();
      return {
        ...data,
        dms: tsToMsAny(data.date) ?? -1
      };
    });

    // 3) Kayıtları tarihe göre (eskiden yeniye) sırala
    // Bu sayede döngüde ilerledikçe hep daha güncel olanı "best" olarak seçeceğiz
    allRecords.sort((a, b) => a.dms - b.dms);

    let best = null;

    for (const data of allRecords) {
      // SADECE seçilen tarihten önce veya o GÜNÜN İÇİNDE olanları al
      // (Eğer 30.01 giriyorsan ve sistemde 30.01 varsa, en son 30.01 kaydını alır)
      if (data.dms <= selectedMs) {
        const fixed = getCorrectedKmFromHistory(data.history);
        const base = parseNumber(data.odometerKm);
        const kmVal = fixed != null ? fixed : base;

        // KM veya Saatlerden en az biri varsa bu kaydı "en güncel aday" yap
        if (kmVal != null || data.hour1 != null) {
          best = {
            km: kmVal,
            h1: parseNumber(data.hour1),
            h2: parseNumber(data.hour2),
            dms: data.dms
          };
        }
      }
    }

    return best ? { km: best.km, h1: best.h1, h2: best.h2 } : { km: null, h1: null, h2: null };
  } catch (err) {
    console.error("Veri çekme hatası:", err);
    return { km: null, h1: null, h2: null };
  }
}

async function fetchRangeDataResilient(plate, selectedDate) {
  if (!plate || !selectedDate) return { prev: null, next: null };
  const selectedMs = tsToMsAny(selectedDate);
  if (selectedMs == null) return { prev: null, next: null };

  try {
    const snap = await getDocs(query(collection(db, "fuel_transactions"), where("vehiclePlate", "in", plateQueryValues(plate))));
    let prev = null;
    let next = null;

    snap.docs.forEach(d => {
      const data = d.data() || {};
      if (data.deleted === true) return;
      const dms = tsToMsAny(data.date);
      if (dms == null) return;

      const fixed = getCorrectedKmFromHistory(data.history);
      const base = parseNumber(data.odometerKm);
      const row = {
        km: fixed != null ? fixed : base,
        h1: parseNumber(data.hour1),
        h2: parseNumber(data.hour2),
        dms
      };

      if (dms < selectedMs && (!prev || dms > prev.dms)) prev = row;
      if (dms > selectedMs && (!next || dms < next.dms)) next = row;
    });

    return { prev, next };
  } catch (err) {
    console.error("Tarih araligi kontrol hatasi:", err);
    return { prev: null, next: null };
  }
}

/* ===================== COMPONENT ===================== */
function DolumEkle(){
  const [role,setRole]=useState(null);
  const [plates,setPlates]=useState([]);
  const [people,setPeople]=useState([]);
  const [tanks,setTanks]=useState([]);
  const [tesisFromColl,setTesisFromColl]=useState([]);

  const [satirlar,setSatirlar]=useState([{
    tarih:getDun(), tesis:"", plaka:"", litre:"", soforLabel:"", soforId:"",
    fisNo:"", km:"", lastKm:null, vehicleTesis:"", kmError:""
  }]);

  const [lastOdoMap,setLastOdoMap]=useState({});

  useEffect(()=>{
    fetchUserMazotRole(auth.currentUser?.email).then(setRole);
    Promise.all([fetchActivePlates(),fetchActivePeople(),fetchActiveTanks(),fetchTesisler()])
      .then(([p,pe,tk,te])=>{ setPlates(p); setPeople(pe); setTanks(tk); setTesisFromColl(te); });
  },[]);

  // tesis -> tank listesi
  const tankByTesis=useMemo(()=>{
    const m=new Map();
    tanks.forEach(t=>{
      const key=normalize(t.tesis||"");
      if(!m.has(key)) m.set(key,[]);
      m.get(key).push(t);
    });
    return m;
  },[tanks]);

  // datalist: tesisler ∪ tank.tesis
  const tesisOptions=useMemo(()=>{
    const set=new Set();
    tesisFromColl.forEach(n=> n && set.add(n));
    tanks.forEach(t=> t.tesis && set.add(t.tesis));
    return Array.from(set).sort((a,b)=>a.localeCompare(b,"tr"));
  },[tesisFromColl,tanks]);

  // plaka -> vehicleTesis
  const vehicleTesisByPlate=useMemo(()=>{
    const m=new Map();
    plates.forEach(p=> m.set(normalizePlate(p.plate), p.vehicleTesis||""));
    return m;
  },[plates]);

  const personLabel=(p)=>`${p.ad}${p.soyad? " "+p.soyad : ""}`;
  const personByLabel=useMemo(()=>{
    const m=new Map();
    people.forEach(pe=> m.set(personLabel(pe), pe));
    return m;
  },[people]);

  const toplamLitre=satirlar.reduce((a,s)=>a+(parseNumber(s.litre)||0),0);

  const getPrevKmForRow=(idx)=>{
    const s=satirlar[idx];
    const plate=normalizePlate(s.plaka||"");
    if(!plate) return null;
    for(let j=idx-1;j>=0;j--){
      const sj=satirlar[j];
      if(normalizePlate(sj.plaka||"")===plate){
        const kmJ=parseNumber(sj.km);
        if(kmJ!=null) return kmJ;
      }
    }
    const fallback = s.lastKm ?? (lastOdoMap[plate] ?? null);
    return fallback ?? null;
  };

  const getRowL100=(idx)=>{
    const lt=parseNumber(satirlar[idx].litre);
    const kmNow=parseNumber(satirlar[idx].km);
    const prevKm=getPrevKmForRow(idx);
    if(lt!=null && lt>0 && kmNow!=null && prevKm!=null && kmNow>prevKm){
      return (lt/(kmNow-prevKm))*100;
    }
    return null;
  };

  const {avgL100, countRows}=useMemo(()=>{
    let ltSum=0, kmSum=0, cnt=0;
    satirlar.forEach((_,i)=>{
      const lt=parseNumber(satirlar[i].litre);
      const kmNow=parseNumber(satirlar[i].km);
      const prevKm=getPrevKmForRow(i);
      if(lt!=null && lt>0 && kmNow!=null && prevKm!=null && kmNow>prevKm){
        ltSum+=lt;
        kmSum+=(kmNow-prevKm);
        cnt++;
      }
    });
    return {avgL100: kmSum>0 ? (ltSum/kmSum)*100 : null, countRows: cnt};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[satirlar,lastOdoMap]);

  const tarihBazinda=useMemo(()=>{
    const m=new Map();
    satirlar.forEach(s=>{
      const key=s.tarih||"-";
      m.set(key,(m.get(key)||0)+(parseNumber(s.litre)||0));
    });
    return Array.from(m.entries()).sort((a,b)=>a[0].localeCompare(b[0]));
  },[satirlar]);

  const handleChange=(i,key,value)=>{
    const y=[...satirlar];


    if(key==="tarih") {
      y[i].tarih=value;
      y[i].lastKm=null; y[i].lastSaat1=null; y[i].lastSaat2=null;
      y[i].nextKm=null; y[i].nextSaat1=null; y[i].nextSaat2=null;
      y[i].kmError="";
    }
    if(key==="tesis") y[i].tesis=value;
    // handleChange fonksiyonunun içine ekle:
if (key === "saat1") y[i].saat1 = value;
if (key === "saat2") y[i].saat2 = value;

    if (key === "plaka") {
      y[i].plaka = normalizePlate(value || "");
      const plate = y[i].plaka;

      // Araç tesisi bilgisi aynen kalsın
      y[i].vehicleTesis = vehicleTesisByPlate.get(plate) || "";

      // KM hatası temizle, lastKm'i blur'da dolduracağız
      y[i].kmError = "";
      y[i].lastKm=null; y[i].lastSaat1=null; y[i].lastSaat2=null;
      y[i].nextKm=null; y[i].nextSaat1=null; y[i].nextSaat2=null;
    }


    if(key==="litre"){
      const v=(value||"").replace(",",".");
      if(v && !/^\d*\.?\d*$/.test(v)) return;
      y[i].litre=v;
    }

    if(key==="soforLabel"){
      y[i].soforLabel=value;
      const found=personByLabel.get(value);
      y[i].soforId=found?found.id:"";
    }

    if (key === "fisNo") {
  y[i].fisNo = (value || "")
    .toUpperCase()
    .replace(/\s+/g, ""); // ❌ boşluk yok
}


    if(key==="km"){
      if(value && !/^\d*$/.test(value)) return;
      y[i].km=value||"";
      y[i].kmError="";
    }

    const prev=satirlar[i-1];
    if(prev){
      if(!y[i].tesis) y[i].tesis=prev.tesis;
      if(!y[i].tarih) y[i].tarih=prev.tarih;
      if(!y[i].fisNo && prev.fisNo) y[i].fisNo=incReceiptNo(prev.fisNo);
    }

    setSatirlar(y);

    if(i===satirlar.length-1){
      const s=y[i];
      const requiredOk = s.tarih && s.tesis && s.plaka && s.litre && s.soforId && s.fisNo && s.km;
      if(requiredOk){
        setSatirlar(prev=>[...prev,{
          tarih:s.tarih, tesis:s.tesis, plaka:"", litre:"",
          soforLabel:"", soforId:"", fisNo:incReceiptNo(s.fisNo), km:"",
          lastKm:null, vehicleTesis:"", kmError:""
        }]);
      }
    }
  };

const handlePlakaBlur = async (i) => {
  const row = satirlar[i];
  const plate = normalizePlate(row.plaka || "");
  if (!plate) return;

  const vehicle = plates.find(p => normalizePlate(p.plate) === plate);
// handlePlakaBlur içinde bu satırı bul ve bununla değiştir:
const type = vehicle ? (vehicle.aracCinsi || "").toString().toUpperCase() : "";

  // row.tarih değerini doğrudan gönderiyoruz, fonksiyon içinde ms'ye çevriliyor
  const rangeData = await fetchRangeDataResilient(plate, row.tarih);
  const lastData = rangeData.prev || { km: null, h1: null, h2: null };

  setSatirlar(prev => {
    const copy = [...prev];
    if (!copy[i]) return prev;
    copy[i] = { 
      ...copy[i], 
      lastKm: lastData.km,
      lastSaat1: lastData.h1,
      lastSaat2: lastData.h2,
      nextKm: rangeData.next?.km ?? null,
      nextSaat1: rangeData.next?.h1 ?? null,
      nextSaat2: rangeData.next?.h2 ?? null,
      vehicleType: type 
    };
    return copy;
  });
};

  const handleKaydet=async()=>{
    const canCreateNow=[ROLES.ONLY_INPUT,ROLES.INPUT_APPROVE,ROLES.CREATOR].includes(role);
    if(!canCreateNow) return alert("Bu işlem için yetkiniz yok.");

// ===============================
// TESİS & PLAKA ZORUNLU KONTROL
// ===============================

// Tesis kontrolü
const tesisSet = new Set(tesisOptions.map(t => normalize(t)));

for (const s of satirlar) {
  if (s.tesis && !tesisSet.has(normalize(s.tesis))) {
    alert(`Geçersiz tesis: ${s.tesis}`);
    return;
  }
}

// Plaka kontrolü
const plateSet = new Set(plates.map(p => normalizePlate(p.plate)));

for (const s of satirlar) {
  if (s.plaka && !plateSet.has(normalizePlate(s.plaka))) {
    alert(`Geçersiz plaka: ${s.plaka}`);
    return;
  }
}


    const invalidIdx=[];
    const nextRows=satirlar.map((s,idx)=>{
      const kmN=parseNumber(s.km);
      const prevKm=getPrevKmForRow(idx);
      const kmError = s.plaka && kmN!=null && prevKm!=null && kmN<prevKm ? `KM ${prevKm}’den küçük olamaz` : "";
      if(kmError) invalidIdx.push(idx);
      return {...s, kmError};
    });
    setSatirlar(nextRows);

    const candidates=nextRows.map((s,idx)=>({
      ...s,
      _idx:idx,
      litreN:parseNumber(s.litre),
      kmN:parseNumber(s.km),
      tarihObj:s.tarih?new Date(`${s.tarih}T12:00:00`):null,
    })).filter(s=> s.tarihObj && s.tesis && s.plaka && s.litreN && s.soforId && s.fisNo && s.kmN!==null && !s.kmError);

    if(candidates.length===0 && invalidIdx.length>0){
      alert(`⚠ Kaydet: ${invalidIdx.length} satır KM nedeniyle hatalı. Kırmızı satırları düzeltip tekrar deneyin.`);
      return;
    }

    const rangeCheckedRows=[...nextRows];
    const rangeErrors=[];
    const hourWarnings=[];
    for(const s of candidates){
      const plate=normalizePlate(s.plaka||"");
      const range=await fetchRangeDataResilient(plate, s.tarih);
      const prevKm=range.prev?.km ?? null;
      const nextKm=range.next?.km ?? null;
      const h1=parseNumber(s.saat1);
      const h2=parseNumber(s.saat2);
      const prevH1=range.prev?.h1 ?? null;
      const nextH1=range.next?.h1 ?? null;
      const prevH2=range.prev?.h2 ?? null;
      const nextH2=range.next?.h2 ?? null;
      let kmError="";

      if(prevKm!=null && s.kmN<prevKm) kmError=`KM onceki kayittan kucuk olamaz. Onceki: ${prevKm}`;
      if(nextKm!=null && s.kmN>nextKm) kmError=`KM sonraki kayittan buyuk olamaz. Sonraki: ${nextKm}`;
      if(kmError) rangeErrors.push(`${s.plaka} / ${s.tarih}: ${kmError}`);

      if(h1!=null && prevH1!=null && h1<prevH1) hourWarnings.push(`${s.plaka} / ${s.tarih}: Arac saati onceki kayittan kucuk (${prevH1}).`);
      if(h1!=null && nextH1!=null && h1>nextH1) hourWarnings.push(`${s.plaka} / ${s.tarih}: Arac saati sonraki kayittan buyuk (${nextH1}).`);
      if((s.vehicleType||"").includes("POMPA")){
        if(h2!=null && prevH2!=null && h2<prevH2) hourWarnings.push(`${s.plaka} / ${s.tarih}: Makine saati onceki kayittan kucuk (${prevH2}).`);
        if(h2!=null && nextH2!=null && h2>nextH2) hourWarnings.push(`${s.plaka} / ${s.tarih}: Makine saati sonraki kayittan buyuk (${nextH2}).`);
      }

      rangeCheckedRows[s._idx]={
        ...rangeCheckedRows[s._idx],
        lastKm:prevKm,
        lastSaat1:prevH1,
        lastSaat2:prevH2,
        nextKm,
        nextSaat1:nextH1,
        nextSaat2:nextH2,
        kmError
      };
    }
    if(rangeErrors.length){
      setSatirlar(rangeCheckedRows);
      alert(rangeErrors.join("\n"));
      return;
    }
    if(hourWarnings.length){
      const ok=window.confirm(`${hourWarnings.join("\n")}\n\nYine de kaydedilsin mi?`);
      if(!ok) {
        setSatirlar(rangeCheckedRows);
        return;
      }
    }

    const cnt=candidates.reduce((m,s)=>{m[s.fisNo]=(m[s.fisNo]||0)+1; return m;}, {});
    const dup=Object.entries(cnt).filter(([,c])=>c>1).map(([k])=>k);
    if(dup.length){
      alert(`Ayni fis no birden cok satirda kullanilamaz: ${dup.join(", ")}`);
      return;
    }

    for(const s of candidates){
      try{
        const qDup=query(collection(db,"fuel_transactions"), where("receiptNo","==",s.fisNo));
        const snap=await getDocs(qDup);
        const exists=snap.docs.some(d => d.data()?.deleted !== true);
        if(exists){
          alert(`Fis no zaten kayitli: ${s.fisNo}. Ayni fis numarasiyla kayit yapilamaz.`);
          return;
        }
      }catch{
        alert(`Fis no kontrolu yapilamadi: ${s.fisNo}. Kayit durduruldu.`);
        return;
      }
    }

    const payloads=[];
    for(const s of candidates){
      const key=normalize(s.tesis);
      const arr=(tankByTesis.get(key))||[];
      const tank=arr.length>0?arr[0]:null;

      payloads.push({
        sourceType:"tank",
        tesis:s.tesis,
        tankId:tank?tank.id:null,
        vehiclePlate:normalizePlate(s.plaka),
        vehicleTesis:s.vehicleTesis||"",
        driverId:s.soforId,
        driverName:s.soforLabel||null,
        date:Timestamp.fromDate(s.tarihObj),
        liters:s.litreN,
        receiptNo:s.fisNo,
        odometerKm:s.kmN,
        createdBy:auth.currentUser?.email||"anonim",
        createdAt:serverTimestamp(),
        deleted:false,
        status:[ROLES.CREATOR,ROLES.INPUT_APPROVE, ROLES.ONLY_INPUT].includes(role)?"approved":"pending",
        // payloads.push içindeki listenin içine ekle:
hour1: parseNumber(s.saat1),
// handleKaydet içindeki payloads.push kısmında bul ve değiştir:
hour2: (s.vehicleType || "").includes("POMPA") ? parseNumber(s.saat2) : null,
      });
    }

    for(const p of payloads) await addDoc(collection(db,"fuel_transactions"), p);

    const savedCount=payloads.length;
    const skippedCount=invalidIdx.length;
    alert(`✅ Kaydedilen: ${savedCount}${skippedCount?`  |  ⛔ Atlanan (KM hatalı): ${skippedCount}`:""}`);

    if(savedCount>0){
      setSatirlar(prev=>[{
        tarih:prev[0]?.tarih||getDun(),
        tesis:prev[0]?.tesis||"",
        plaka:"", litre:"", soforLabel:"", soforId:"",
        fisNo:incReceiptNo(prev.find(s=>s.fisNo)?.fisNo||""),
        km:"", lastKm:null, vehicleTesis:"", kmError:""
      }]);
    }
  };

  return (
    <div style={{padding:20,overflowX:"auto"}}>
      <h2 style={{textAlign:"left",color:"#334155",fontSize:22,fontWeight:"bold",marginBottom:20}}>Araç Dolumu (Toplu Giriş)</h2>

      <table style={{borderCollapse:"collapse",width:"100%",minWidth:"1300px",fontSize:"13px"}}>
{/* Başlık dizisini şu şekilde değiştirerek Araç ve Makine saatlerini ekle */}
<thead style={{backgroundColor:"#e2e8f0"}}>
  <tr>
    {["Tarih","Tesis","Plaka","Litre","Şoför","Fiş No","KM","Araç Saati","Makine Saati","Son KM (bilgi)","L/100km","Araç Tesisi"]
      .map((h,i)=>(
        <th key={i} style={{border:"1px solid #ccc",padding:"8px",textAlign:"center"}}>{h}</th>
      ))}
  </tr>
</thead>
        <tbody>
          {satirlar.map((s,i)=>{
            const prevKm=getPrevKmForRow(i);
            const l100=getRowL100(i);
            const deltaKm=(parseNumber(s.km)!=null && prevKm!=null) ? (parseNumber(s.km)-prevKm) : null;

            return (
              <tr key={i} style={{backgroundColor: s.kmError ? "#fee2e2" : "transparent"}}>
                <td style={{border:"1px solid #ccc",padding:"4px"}}>
                  <input type="date" value={s.tarih}
                    onChange={e=>handleChange(i,"tarih",e.target.value)}
                    style={{width:"100%",border:"none",outline:"none",fontSize:"12px"}}/>
                </td>

                <td style={{border:"1px solid #ccc",padding:"4px"}}>
                  <input list="dl-tesisler" value={s.tesis}
                    onChange={e=>handleChange(i,"tesis",e.target.value)}
                    style={{width:"100%",border:"none",outline:"none",fontSize:"12px"}}/>
                </td>

<td style={{border:"1px solid #ccc",padding:"4px"}}>
  <input
    list="dl-plakalar"
    value={s.plaka}
    onChange={e => handleChange(i, "plaka", e.target.value)}
   
    onBlur={() => handlePlakaBlur(i)}
    style={{width:"100%",border:"none",outline:"none",fontSize:"12px"}}
  />
</td>


                <td style={{border:"1px solid #ccc",padding:"4px"}}>
                  <input type="text" value={s.litre}
                    onChange={e=>handleChange(i,"litre",e.target.value)}
                    style={{width:"100%",border:"none",outline:"none",fontSize:"12px"}}/>
                </td>

                <td style={{border:"1px solid #ccc",padding:"4px"}}>
                  <input list="dl-soforler" value={s.soforLabel}
                    onChange={e=>handleChange(i,"soforLabel",e.target.value)}
                    style={{width:"100%",border:"none",outline:"none",fontSize:"12px"}}/>
                </td>

                <td style={{border:"1px solid #ccc",padding:"4px"}}>
                  <input type="text" value={s.fisNo}
                    onChange={e=>handleChange(i,"fisNo",e.target.value)}
                    style={{width:"100%",border:"none",outline:"none",fontSize:"12px"}}/>
                </td>

                <td style={{border:"1px solid #ccc",padding:"4px"}}>
                  <input type="text" value={s.km}
                    onChange={e=>handleChange(i,"km",e.target.value)}
                    style={{
                      width:"100%",
                      border: s.kmError ? "1px solid #ef4444" : "none",
                      backgroundColor: s.kmError ? "#ffe4e6" : "transparent",
                      outline:"none", fontSize:"12px"
                    }}/>
                  {s.kmError && <div style={{ color:"#b91c1c", fontSize:11, marginTop:2 }}>{s.kmError}</div>}
                </td>


{/* BURADAN İTİBAREN YENİ SAAT HÜCRELERİ BAŞLIYOR */}
{/* ARAÇ SAATİ (Hem mikser hem pompa için) */}
<td style={{ border: "1px solid #ccc", padding: "4px" }}>
  <input
    type="text"
    placeholder="Araç Saati"
    value={s.saat1 || ""}
    onChange={(e) => handleChange(i, "saat1", e.target.value)}
    style={{ width: "100%", border: "none", outline: "none", fontSize: "12px" }}
  />
  <div style={{ color: "#64748b", fontSize: "10px" }}>Son: {s.lastSaat1 ?? "-"}</div>
</td>

{/* MAKİNE SAATİ (Sadece Pompalar için aktif) */}
<td style={{ 
  border: "1px solid #ccc", 
  padding: "4px", 
  backgroundColor: !(s.vehicleType || "").includes("POMPA") ? "#f1f5f9" : "transparent" 
}}>
  <input
    type="text"
    placeholder={(s.vehicleType || "").includes("POMPA") ? "Makine Saati" : "X"}
    value={s.saat2 || ""}
    disabled={!(s.vehicleType || "").includes("POMPA")}
    onChange={(e) => handleChange(i, "saat2", e.target.value)}
    style={{ width: "100%", border: "none", outline: "none", fontSize: "12px", backgroundColor: "transparent" }}
  />
  {(s.vehicleType || "").includes("POMPA") && (
    <div style={{ color: "#64748b", fontSize: "10px" }}>Son: {s.lastSaat2 ?? "-"}</div>
  )}
</td>
{/* YENİ HÜCRELER BURADA BİTTİ */}

{/* SON KM BİLGİ HÜCRESİ (Zaten dosvanda olan kısım) */}
<td style={{border:"1px solid #ccc",padding:"4px",color:"#64748b",fontSize:"12px"}}>
  {prevKm!=null ? prevKm : "-"}
</td>



                <td style={{border:"1px solid #ccc",padding:"4px",textAlign:"right"}}
                    title={deltaKm!=null ? `ΔKM: ${fmt(deltaKm)}` : ""}>
                  {l100!=null ? fmt(l100) : "-"}
                </td>

                <td style={{border:"1px solid #ccc",padding:"4px",color:"#334155",fontSize:"12px"}}>
                  {s.vehicleTesis || "-"}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {/* Datalist kaynakları */}
      <datalist id="dl-tesisler">{tesisOptions.map(name=><option key={name} value={name}/>)}</datalist>
      <datalist id="dl-plakalar">{plates.map(p=><option key={p.id} value={p.plate}/>)}</datalist>
      <datalist id="dl-soforler">{people.map(pe=><option key={pe.id} value={`${pe.ad}${pe.soyad? " "+pe.soyad:""}`}/>)}</datalist>

      <div style={{marginTop:12,fontSize:"13px",fontWeight:"bold"}}>
        Toplam Girilen Litre: {fmt(toplamLitre)} L{" · "}
        Genel Ortalama: {avgL100!=null ? `${fmt(avgL100)} L/100km` : "-"}{" "}
        {countRows ? `( ${countRows} satır )` : ""}
      </div>

      <div style={{marginTop:6,fontSize:"12px",color:"#334155"}}>
        {tarihBazinda.map(([tarih,lt])=>(
          <div key={tarih}>{tarih}: <b>{fmt(lt)}</b> L</div>
        ))}
      </div>

      <div style={{marginTop:16}}>
        <button onClick={handleKaydet}
          style={{backgroundColor:"#4f46e5",color:"white",padding:"10px 20px",fontWeight:"bold",borderRadius:6,border:"none",cursor:"pointer"}}>
          Kaydet
        </button>
      </div>
    </div>
  );
}

/* ---- dosya sonu: default export ---- */
export default DolumEkle;
